'use client';

/* ═══════════════════════════════════════════════════
   JacquardI18nProvider — Internationalisation context (ADR-025)

   Manages locale, text direction, time zone, and optional
   message overrides. Sets `dir` and `lang` on <html> so
   CSS logical properties respond immediately.

   SSR integration (ADR-023 §5):
   When `<JacquardScript />` runs before hydration, it sets
   `window.__JACQUARD__` with the resolved theme/dir/locale.
   When `<html lang dir>` is rendered directly by the server
   template, those attributes carry the same truth. The
   provider reads BOTH sources before falling back to its
   documented defaults so that:
     (a) DOM attrs are not clobbered post-hydration when the
         caller omits the matching prop (audit Finding #1).
     (b) `useJacquardI18n()` returns the same value adopters
         see in the DOM, not a stale en-US/ltr default.

   Usage:
     <JacquardI18nProvider locale="ar-SA" direction="auto">
       <App />
     </JacquardI18nProvider>
   ═══════════════════════════════════════════════════ */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';

export interface JacquardI18nConfig {
  locale: string;
  fallbackLocale?: string;
  /** Resolved text direction. `auto` is a REQUEST, never a result —
   *  the provider always normalises it to `ltr` or `rtl` in context. */
  direction?: 'ltr' | 'rtl' | 'auto';
  timeZone?: string;
  messages?: Record<string, string>;
}

export interface JacquardI18nProviderProps extends Partial<JacquardI18nConfig> {
  children: ReactNode;
}

const DEFAULT_I18N: JacquardI18nConfig = {
  locale: 'en-US',
  fallbackLocale: 'en-US',
  direction: 'ltr',
};

export const JacquardI18nContext = createContext<JacquardI18nConfig>(DEFAULT_I18N);

export function useJacquardI18n(): JacquardI18nConfig {
  return useContext(JacquardI18nContext);
}

/**
 * Resolve the text direction for a locale when `direction` is `'auto'`.
 *
 * Tries the Intl.Locale `textInfo` API first (Chrome 99+, Node 21+),
 * then falls back to a hardcoded list of common RTL language codes.
 */
function resolveDirection(locale: string): 'ltr' | 'rtl' {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const info = (new Intl.Locale(locale) as any).textInfo;
    if (info?.direction === 'rtl') return 'rtl';
  } catch {
    /* Intl.Locale may throw for malformed tags — fall through. */
  }
  const rtlLocales = ['ar', 'he', 'fa', 'ur', 'ps', 'ku', 'sd', 'yi'];
  const lang = locale.split('-')[0].toLowerCase();
  return rtlLocales.includes(lang) ? 'rtl' : 'ltr';
}

/* ── SSR bridge: read window.__JACQUARD__ set by JacquardScript ── */

interface JacquardSnapshot {
  theme?: string;
  dir?: string;
  locale?: string;
  density?: 'compact' | 'comfortable' | null;
}

declare global {
  interface Window {
    __JACQUARD__?: JacquardSnapshot;
  }
}

function getScriptSnapshot(): JacquardSnapshot | null {
  return typeof window !== 'undefined' ? (window.__JACQUARD__ ?? null) : null;
}

function getServerSnapshot(): JacquardSnapshot | null {
  return null;
}

const NOOP_SUBSCRIBE = () => () => {};

export function JacquardI18nProvider({
  locale,
  fallbackLocale = 'en-US',
  direction,
  timeZone,
  messages,
  children,
}: JacquardI18nProviderProps) {
  /* Read the snapshot JacquardScript wrote pre-paint. useSyncExternalStore
     keeps the read SSR-safe: the server snapshot is `null`, matching the
     first client render so hydration doesn't diverge. Subsequent reads
     pick up the populated snapshot. */
  const snapshot = useSyncExternalStore(NOOP_SUBSCRIBE, getScriptSnapshot, getServerSnapshot);

  /* Server-template fallback: when an adopter renders `<html lang dir>`
     directly in their SSR output (without JacquardScript), the DOM
     attributes are the pre-paint truth. We can't read them during the
     first client render — that would diverge from the server render and
     trigger a React hydration mismatch — so defer to a useEffect.
     Mirrors the codex audit #108 R7 pattern in JacquardProvider. */
  const [domLang, setDomLang] = useState<string | null>(null);
  const [domDir, setDomDir] = useState<'ltr' | 'rtl' | null>(null);
  /* Flipped by the same one-shot effect that reads the DOM. Marks
     "first hydration pass is complete" so the auto-direction guard
     below can stop deferring even when no real locale source ever
     materializes (no prop, no script snapshot, no <html lang>). */
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    const root = document.documentElement;
    const lang = root.getAttribute('lang');
    if (lang) setDomLang(lang);
    const dir = root.getAttribute('dir');
    if (dir === 'ltr' || dir === 'rtl') setDomDir(dir);
    setHydrated(true);
  }, []);

  /* Effective locale: explicit prop > script snapshot > server-rendered
     DOM attr > documented default. The context value reflects this so
     consumers reading useJacquardI18n() see the SAME locale the DOM
     does — never a stale default that contradicts the visible page. */
  const effectiveLocale = locale ?? snapshot?.locale ?? domLang ?? 'en-US';

  /* Effective direction:
     - explicit 'ltr' / 'rtl' wins outright
     - explicit 'auto' resolves against effectiveLocale (NOT just
       'en-US' — that was the audit-round-2 high-risk finding)
     - omitted prop falls back to the snapshot, then the DOM, then 'ltr'. */
  const effectiveDirection: 'ltr' | 'rtl' =
    direction === 'ltr' || direction === 'rtl'
      ? direction
      : direction === 'auto'
        ? resolveDirection(effectiveLocale)
        : snapshot?.dir === 'ltr' || snapshot?.dir === 'rtl'
          ? snapshot.dir
          : (domDir ?? 'ltr');

  /* During real SSR hydration, `useSyncExternalStore` returns the
     server snapshot (`null`) on the first client render, and the
     deferred DOM read in the useEffect above has not yet populated
     `domLang`. If `direction='auto'` is paired with an omitted
     `locale` prop, the very first effect would `resolveDirection(
     'en-US')` and write `dir='ltr'` — clobbering whatever
     `JacquardScript` set pre-paint (e.g. `dir='rtl'` for `he-IL`).
     The effect re-runs a tick later with the populated snapshot /
     `domLang` and writes the correct direction, but the intermediate
     LTR flash is a visible regression. Guard against it by deferring
     the first auto-direction DOM write until we have a real locale
     source.

     The `!hydrated` clause is critical: the deferral lasts ONLY
     through the first hydration tick. If no locale source ever
     appears (no prop, no script, no <html lang>), the guard releases
     after the hydration effect runs and the next pass falls back to
     resolveDirection('en-US')='ltr'. Without this, an explicit
     `direction='auto'` with no locale source would never write to
     the DOM AND `useJacquardI18n()` would report `direction='ltr'`,
     leaving DOM and context permanently out of sync if pre-paint
     `<html dir>` happened to differ. */
  const localeUnresolved =
    locale === undefined && snapshot?.locale === undefined && domLang === null;
  const skipAutoDirWrite = direction === 'auto' && localeUnresolved && !hydrated;

  useEffect(() => {
    /* Audit Finding #1: when locale/direction are omitted (e.g.,
       JacquardRoot used without an `i18n` prop), do NOT write defaults
       to <html> — JacquardScript may have already applied cookie-derived
       values pre-paint, and blindly writing 'en-US'/'ltr' would clobber
       them post-hydration. We also restore prior values on cleanup
       instead of removing, so StrictMode's mount→unmount→remount cycle
       (and any genuine unmount) leaves the pre-paint state intact. */
    const root = document.documentElement;
    let prevDir: string | null = null;
    let prevLang: string | null = null;
    let dirChanged = false;
    let langChanged = false;

    if (direction !== undefined && !skipAutoDirWrite) {
      /* Use effectiveLocale (which already accounts for snapshot/DOM)
         so direction='auto' without a locale prop still resolves
         against the pre-paint locale instead of falling to en-US. */
      const dir = direction === 'auto' ? resolveDirection(effectiveLocale) : direction;
      prevDir = root.getAttribute('dir');
      root.setAttribute('dir', dir);
      dirChanged = true;
    }
    if (locale !== undefined) {
      prevLang = root.getAttribute('lang');
      root.setAttribute('lang', locale);
      langChanged = true;
    }
    return () => {
      if (dirChanged) {
        if (prevDir !== null) root.setAttribute('dir', prevDir);
        else root.removeAttribute('dir');
      }
      if (langChanged) {
        if (prevLang !== null) root.setAttribute('lang', prevLang);
        else root.removeAttribute('lang');
      }
    };
  }, [locale, direction, effectiveLocale, skipAutoDirWrite]);

  const value = useMemo(
    () => ({
      locale: effectiveLocale,
      fallbackLocale,
      direction: effectiveDirection,
      timeZone,
      messages,
    }),
    [effectiveLocale, fallbackLocale, effectiveDirection, timeZone, messages],
  );

  return <JacquardI18nContext.Provider value={value}>{children}</JacquardI18nContext.Provider>;
}
